import React, { useState, useMemo, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Wallet, ArrowUpFromLine, Copy, Check,
  AlertCircle, TrendingUp, Clock, ChevronDown, Shield, Zap,
  RefreshCw, BadgeCheck, Info,
} from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { THAI_BANKS } from "@/shared/constants/thaiBanks";

/* ─── CONSTANTS ─── */
const USDT_NETWORKS = [
  { code: "TRC20", name: "Tron",     fee: "~1 USDT",   time: "~1 min",  color: "#f87171" },
  { code: "ERC20", name: "Ethereum", fee: "~15 USDT",  time: "~5 min",  color: "#a78bfa" },
  { code: "BEP20", name: "BNB",      fee: "~0.5 USDT", time: "~1 min",  color: "#fbbf24" },
];
const USDT_TO_THB = 35.25;

/* ─── GLOBAL STYLES ─── */
const STYLE = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.wf-root {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background: #0a0f1e;
  color: #e2eaf5;
  min-height: 100vh;
  padding: 24px 16px;
}

/* LAYOUT */
.wf-grid {
  display: grid;
  grid-template-columns: 1fr 310px;
  gap: 20px;
  max-width: 960px;
  margin: 0 auto;
  align-items: start;
}
@media (max-width: 768px) {
  .wf-grid { grid-template-columns: 1fr; }
  .wf-sidebar { order: -1; }
}

/* CARD */
.wf-card {
  background: linear-gradient(145deg, #0e1628 0%, #111827 100%);
  border: 1px solid rgba(34,211,238,0.12);
  border-radius: 20px;
  padding: 28px;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;
}
.wf-card::before {
  content: '';
  position: absolute; top: 0; left: 0; right: 0; height: 1px;
  background: linear-gradient(90deg, transparent 10%, rgba(34,211,238,0.4) 50%, transparent 90%);
}
.wf-card:hover {
  border-color: rgba(34,211,238,0.32);
  box-shadow: 0 0 40px rgba(34,211,238,0.07);
}
@media (max-width: 480px) {
  .wf-card { padding: 18px; border-radius: 16px; }
}

/* TAB BAR */
.wf-tabs {
  display: flex; gap: 6px;
  background: rgba(5,10,20,0.9);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 14px;
  padding: 5px;
  margin-bottom: 24px;
}
.wf-tab {
  flex: 1; padding: 11px 14px; border-radius: 10px;
  border: 1px solid transparent;
  font-size: 13px; font-weight: 700;
  cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 7px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  color: #4a6080;
  background: transparent;
  transition: all 0.25s ease;
}
.wf-tab:hover:not(.wf-tab-active) {
  color: #7aabb8;
  background: rgba(34,211,238,0.04);
}
.wf-tab-active-bank {
  color: #22d3ee !important;
  background: rgba(34,211,238,0.1) !important;
  border-color: rgba(34,211,238,0.28) !important;
  box-shadow: 0 0 20px rgba(34,211,238,0.1);
}
.wf-tab-active-usdt {
  color: #34d399 !important;
  background: rgba(52,211,153,0.1) !important;
  border-color: rgba(52,211,153,0.28) !important;
  box-shadow: 0 0 20px rgba(52,211,153,0.1);
}

/* FORM FIELDS */
.wf-field { display: flex; flex-direction: column; gap: 7px; }
.wf-label {
  font-size: 11px; font-weight: 700;
  color: #5a7890;
  text-transform: uppercase; letter-spacing: 0.1em;
  transition: color 0.2s;
}
.wf-label-focused { color: #22d3ee !important; }

.wf-input-wrap { position: relative; }
.wf-prefix {
  position: absolute; left: 13px; top: 50%; transform: translateY(-50%);
  font-family: 'JetBrains Mono', monospace;
  font-size: 15px; font-weight: 700; color: #22d3ee;
  pointer-events: none; z-index: 2;
}
.wf-input {
  width: 100%;
  background: rgba(5,10,20,0.8);
  border: 1.5px solid rgba(255,255,255,0.07);
  border-radius: 12px;
  padding: 12px 14px;
  color: #d4e8f8;
  font-size: 14px; font-weight: 500;
  font-family: 'Plus Jakarta Sans', sans-serif;
  outline: none;
  transition: all 0.25s ease;
  -moz-appearance: textfield;
}
.wf-input::-webkit-outer-spin-button,
.wf-input::-webkit-inner-spin-button { -webkit-appearance: none; }
.wf-input::placeholder { color: #1e3045; }
.wf-input:hover {
  border-color: rgba(34,211,238,0.25);
  background: rgba(5,10,20,0.95);
}
.wf-input:focus {
  border-color: rgba(34,211,238,0.55) !important;
  background: rgba(34,211,238,0.03);
  box-shadow: 0 0 0 3px rgba(34,211,238,0.08), 0 0 20px rgba(34,211,238,0.05);
  color: #e8f4ff;
}
.wf-input-mono { font-family: 'JetBrains Mono', monospace !important; font-size: 12.5px !important; }
.wf-input-prefix { padding-left: 38px !important; font-family: 'JetBrains Mono', monospace; font-size: 14px; font-weight: 700; }
.wf-hint { font-size: 10.5px; color: #2a4560; font-weight: 500; }

/* BANK DROPDOWN */
.wf-select-btn {
  width: 100%;
  background: rgba(5,10,20,0.8);
  border: 1.5px solid rgba(255,255,255,0.07);
  border-radius: 12px; padding: 12px 14px;
  color: #d4e8f8; font-size: 14px; font-weight: 500;
  cursor: pointer; display: flex; align-items: center; justify-content: space-between;
  font-family: 'Plus Jakarta Sans', sans-serif;
  outline: none; transition: all 0.25s ease;
}
.wf-select-btn:hover, .wf-select-open {
  border-color: rgba(34,211,238,0.45) !important;
  background: rgba(34,211,238,0.03) !important;
  box-shadow: 0 0 0 3px rgba(34,211,238,0.07);
}
.wf-dropdown {
  position: absolute; top: calc(100% + 6px); left: 0; right: 0; z-index: 300;
  background: #0c1525;
  border: 1.5px solid rgba(34,211,238,0.2);
  border-radius: 14px; padding: 6px;
  max-height: 220px; overflow-y: auto;
  box-shadow: 0 24px 60px rgba(0,0,0,0.8), 0 0 40px rgba(34,211,238,0.06);
  scrollbar-width: thin; scrollbar-color: rgba(34,211,238,0.15) transparent;
}
.wf-dropdown::-webkit-scrollbar { width: 3px; }
.wf-dropdown::-webkit-scrollbar-thumb { background: rgba(34,211,238,0.15); border-radius: 3px; }
.wf-bank-opt {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 12px; border-radius: 10px; cursor: pointer;
  border: 1px solid transparent; transition: all 0.15s ease;
  color: #aac8e0; font-size: 13px; font-weight: 600;
}
.wf-bank-opt:hover { background: rgba(34,211,238,0.07); border-color: rgba(34,211,238,0.15); color: #d4e8f8; }
.wf-bank-opt-sel { background: rgba(34,211,238,0.1) !important; border-color: rgba(34,211,238,0.25) !important; color: #22d3ee !important; }

/* NETWORK BUTTONS */
.wf-net-grid { display: flex; gap: 8px; }
.wf-net-btn {
  flex: 1; padding: 12px 8px; border-radius: 12px;
  border: 1.5px solid rgba(255,255,255,0.07);
  background: rgba(5,10,20,0.8);
  cursor: pointer; text-align: center;
  transition: all 0.25s ease;
  font-family: 'Plus Jakarta Sans', sans-serif;
  position: relative; overflow: hidden;
}
.wf-net-btn:hover {
  border-color: rgba(34,211,238,0.3);
  background: rgba(34,211,238,0.05);
  transform: translateY(-3px);
  box-shadow: 0 10px 30px rgba(0,0,0,0.4);
}

/* SUBMIT BUTTON */
.wf-btn {
  width: 100%; border: none; border-radius: 12px; padding: 14px;
  font-size: 14px; font-weight: 800; cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 9px;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: -0.01em; transition: all 0.25s ease;
  position: relative; overflow: hidden;
}
.wf-btn::after {
  content: '';
  position: absolute; inset: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.08) 50%, transparent 100%);
  transform: translateX(-100%);
  transition: transform 0.5s ease;
}
.wf-btn:hover:not(:disabled)::after { transform: translateX(100%); }
.wf-btn:hover:not(:disabled) { transform: translateY(-2px); filter: brightness(1.1); }
.wf-btn:active:not(:disabled) { transform: scale(0.98); }
.wf-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.wf-btn-green {
  background: linear-gradient(135deg, #059669, #10b981);
  box-shadow: 0 6px 24px rgba(16,185,129,0.3);
  color: #fff;
}
.wf-btn-cyan {
  background: linear-gradient(135deg, #0891b2, #22d3ee);
  box-shadow: 0 6px 24px rgba(34,211,238,0.3);
  color: #fff;
}

/* STAT CARDS */
.wf-stat {
  border-radius: 14px; padding: 14px 16px;
  border: 1px solid transparent;
  transition: all 0.3s ease;
  cursor: default;
}
.wf-stat:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0,0,0,0.5);
}

/* PROC ROW */
.wf-proc-row {
  display: flex; align-items: center; justify-content: space-between;
  padding: 10px 0; border-bottom: 1px solid rgba(255,255,255,0.04);
  transition: all 0.2s ease;
}
.wf-proc-row:last-child { border-bottom: none; }
.wf-proc-row:hover { padding-left: 4px; }

/* ALERTS */
.wf-alert {
  border-radius: 12px; padding: 12px 14px;
  display: flex; align-items: flex-start; gap: 10px;
  font-size: 12px; font-weight: 500; line-height: 1.6;
}
.wf-alert-warn {
  background: rgba(251,191,36,0.06);
  border: 1px solid rgba(251,191,36,0.2);
  color: #c8913a;
}
.wf-alert-warn strong { color: #fbbf24; }

/* CONFIRM PILL */
.wf-confirm {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 16px; border-radius: 12px;
  background: rgba(52,211,153,0.07);
  border: 1px solid rgba(52,211,153,0.2);
  transition: all 0.2s ease;
}
.wf-confirm:hover {
  background: rgba(52,211,153,0.1);
  border-color: rgba(52,211,153,0.3);
}

/* RATE CARD */
.wf-rate-card {
  border-radius: 14px; padding: 14px 16px;
  background: linear-gradient(135deg, rgba(34,211,238,0.07) 0%, rgba(99,102,241,0.05) 100%);
  border: 1px solid rgba(34,211,238,0.18);
  display: flex; align-items: center; justify-content: space-between;
  position: relative; overflow: hidden;
}
.wf-rate-card::before {
  content: '';
  position: absolute; top: 0; left: 10%; right: 10%; height: 1px;
  background: linear-gradient(90deg, transparent, rgba(34,211,238,0.3), transparent);
}

/* HEADING */
.wf-heading { display: flex; align-items: center; gap: 12px; margin-bottom: 22px; }
.wf-icon-box {
  width: 40px; height: 40px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
}
.wf-section-title {
  display: flex; align-items: center; gap: 8px; margin-bottom: 14px;
}
.wf-divider {
  width: 3px; height: 14px; border-radius: 2px;
  background: linear-gradient(180deg, #22d3ee, #6366f1);
}

@keyframes wf-spin { to { transform: rotate(360deg); } }
.wf-spinner {
  width: 16px; height: 16px;
  border: 2px solid rgba(255,255,255,0.2);
  border-top-color: #fff;
  border-radius: 50%;
  animation: wf-spin 0.7s linear infinite;
}
`;

/* ─── COMPONENTS ─── */

function Field({ label, focused, children, hint }) {
  return (
    <div className="wf-field">
      {label && <label className={`wf-label${focused ? " wf-label-focused" : ""}`}>{label}</label>}
      {children}
      {hint && <span className="wf-hint">{hint}</span>}
    </div>
  );
}

function TextInput({ label, value, onChange, type = "text", placeholder, mono, prefix, hint, required, disabled }) {
  const [focused, setFocused] = useState(false);
  return (
    <Field label={label ? (label + (required ? " *" : "")) : null} focused={focused} hint={hint}>
      <div className="wf-input-wrap">
        {prefix && <span className="wf-prefix">{prefix}</span>}
        <input
          className={`wf-input${mono ? " wf-input-mono" : ""}${prefix ? " wf-input-prefix" : ""}`}
          type={type} value={value} onChange={onChange}
          placeholder={placeholder} disabled={disabled}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        />
      </div>
    </Field>
  );
}

function BankSelector({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const sel = THAI_BANKS.find(b => b.code === value);

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <Field label="Select Bank *">
      <div style={{ position: "relative" }} ref={ref}>
        <button
          type="button"
          className={`wf-select-btn${open ? " wf-select-open" : ""}`}
          onClick={() => setOpen(v => !v)}
        >
          {sel ? (
            <span style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: sel.color || "#22d3ee", boxShadow: `0 0 6px ${sel.color || "#22d3ee"}`, display: "inline-block" }} />
              <span style={{ color: "#d4e8f8", fontWeight: 600 }}>{sel.name}</span>
              <span style={{ color: "#3a6080", fontSize: "11px", fontFamily: "monospace" }}>({sel.shortName || sel.code})</span>
            </span>
          ) : <span style={{ color: "#2a4560" }}>Choose your bank</span>}
          <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ type: "spring", stiffness: 400, damping: 28 }}>
            <ChevronDown size={15} style={{ color: "#3a6080" }} />
          </motion.span>
        </button>
        <AnimatePresence>
          {open && (
            <motion.div className="wf-dropdown"
              initial={{ opacity: 0, y: -8, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.97 }}
              transition={{ duration: 0.18 }}
            >
              {THAI_BANKS.map((bank, i) => (
                <motion.div key={bank.code}
                  className={`wf-bank-opt${value === bank.code ? " wf-bank-opt-sel" : ""}`}
                  initial={{ opacity: 0, x: -4 }} animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.015 }}
                  onClick={() => { onChange(bank); setOpen(false); }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: "50%", background: bank.color || "#22d3ee", flexShrink: 0, boxShadow: value === bank.code ? `0 0 8px ${bank.color}` : "none", display: "inline-block" }} />
                  <span>{bank.name}</span>
                  <span style={{ color: "#2a4060", fontSize: "10px", fontFamily: "monospace", marginLeft: "auto" }}>{bank.shortName || bank.code}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Field>
  );
}

function StatCard({ label, value, sub, color, icon: Icon, delay = 0 }) {
  return (
    <motion.div className="wf-stat"
      initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.3 }}
      style={{
        background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
        borderColor: `${color}20`,
      }}
      whileHover={{ borderColor: `${color}45`, boxShadow: `0 12px 40px ${color}15` }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <span style={{ color: "#4a6880", fontSize: "10px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>{label}</span>
        {Icon && (
          <div style={{
            width: 28, height: 28, borderRadius: 8,
            background: `${color}14`, border: `1px solid ${color}22`,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <Icon size={12} style={{ color }} />
          </div>
        )}
      </div>
      <p style={{ color, fontSize: "20px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.03em" }}>{value}</p>
      {sub && <p style={{ color: "#2a4060", fontSize: "10.5px", marginTop: "4px", fontWeight: 600 }}>{sub}</p>}
    </motion.div>
  );
}

/* ─── MAIN ─── */
export default function WithdrawalForm({
  onSubmit,
  submitting = false,
  stats = { total: 0, pendingAmount: 0, pendingCount: 0 },
  accentColor = "emerald",
}) {
  let t = null;
  try { const lc = useLanguage(); t = lc?.t; } catch {}

  const [activeTab, setActiveTab] = useState("bank");
  const [bankForm, setBankForm] = useState({ bank_code: "", bank_name: "", account_number: "", account_name: "", amount: "" });
  const [usdtForm, setUsdtForm] = useState({ network: "TRC20", wallet_address: "", amount: "" });
  const [copied, setCopied] = useState(false);

  const thbEq = useMemo(() => (parseFloat(usdtForm.amount) || 0) * USDT_TO_THB, [usdtForm.amount]);
  const isEmerald = accentColor === "emerald";

  const handleBankSubmit = e => {
    e.preventDefault();
    if (!bankForm.bank_code || !bankForm.account_number || !bankForm.amount) return;
    onSubmit({ type: "bank", ...bankForm, amount: parseFloat(bankForm.amount) });
    setBankForm({ bank_code: "", bank_name: "", account_number: "", account_name: "", amount: "" });
  };

  const handleUsdtSubmit = e => {
    e.preventDefault();
    if (!usdtForm.wallet_address || !usdtForm.amount) return;
    onSubmit({ type: "usdt", ...usdtForm, amount: parseFloat(usdtForm.amount), thb_equivalent: thbEq });
    setUsdtForm({ network: "TRC20", wallet_address: "", amount: "" });
  };

  const copyAddr = () => {
    navigator.clipboard.writeText(usdtForm.wallet_address);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const bankValid = bankForm.bank_code && bankForm.account_number && bankForm.amount;
  const usdtValid = usdtForm.wallet_address && usdtForm.amount;

  return (
    <>
      <style>{STYLE}</style>
      <div className="wf-root">
        <div className="wf-grid">

          {/* ── MAIN FORM ── */}
          <motion.div className="wf-card"
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          >
            {/* Heading */}
            <div className="wf-heading">
              <div className="wf-icon-box" style={{
                background: isEmerald
                  ? "linear-gradient(135deg, #059669, #10b981)"
                  : "linear-gradient(135deg, #0891b2, #22d3ee)",
                boxShadow: isEmerald
                  ? "0 6px 20px rgba(16,185,129,0.35)"
                  : "0 6px 20px rgba(34,211,238,0.35)",
              }}>
                <ArrowUpFromLine size={18} color="#fff" />
              </div>
              <div>
                <h2 style={{ fontSize: "16px", fontWeight: 800, color: "#e8f4ff", letterSpacing: "-0.02em" }}>
                  Request Withdrawal
                </h2>
                <p style={{ fontSize: "12px", color: "#3a6080", marginTop: 3, fontWeight: 500 }}>
                  Transfer funds to your bank or crypto wallet
                </p>
              </div>
            </div>

            {/* Tabs */}
            <div className="wf-tabs">
              {[
                { id: "bank", icon: Building2, label: "Bank Transfer" },
                { id: "usdt", icon: Wallet, label: "USDT Crypto" },
              ].map(({ id, icon: Icon, label }) => {
                const isActive = activeTab === id;
                const activeClass = isEmerald ? "wf-tab-active-bank" : id === "bank" ? "wf-tab-active-bank" : "wf-tab-active-usdt";
                return (
                  <motion.button key={id}
                    className={`wf-tab${isActive ? ` ${activeClass}` : ""}`}
                    onClick={() => setActiveTab(id)}
                    whileTap={{ scale: 0.96 }}
                  >
                    <Icon size={14} /> {label}
                  </motion.button>
                );
              })}
            </div>

            <AnimatePresence mode="wait">
              {/* BANK FORM */}
              {activeTab === "bank" && (
                <motion.form key="bank"
                  initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }}
                  transition={{ duration: 0.22 }}
                  onSubmit={handleBankSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  <BankSelector value={bankForm.bank_code} onChange={bank => setBankForm({ ...bankForm, bank_code: bank.code, bank_name: bank.name })} />

                  <TextInput label="Account Number" value={bankForm.account_number}
                    onChange={e => setBankForm({ ...bankForm, account_number: e.target.value })}
                    placeholder="Enter bank account number" mono required />

                  <TextInput label="Account Holder Name" value={bankForm.account_name}
                    onChange={e => setBankForm({ ...bankForm, account_name: e.target.value })}
                    placeholder="Full name as on account" />

                  <TextInput label="Withdrawal Amount (THB)" value={bankForm.amount}
                    onChange={e => setBankForm({ ...bankForm, amount: e.target.value })}
                    type="number" prefix="฿" placeholder="0.00"
                    hint="Minimum withdrawal: ฿100" required />

                  <AnimatePresence>
                    {bankForm.amount && parseFloat(bankForm.amount) >= 100 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.22 }}
                      >
                        <div className="wf-confirm">
                          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <BadgeCheck size={15} style={{ color: "#34d399" }} />
                            <span style={{ color: "#7ac8a8", fontSize: "12.5px", fontWeight: 600 }}>You will receive</span>
                          </span>
                          <span style={{ color: "#34d399", fontSize: "15px", fontWeight: 800, fontFamily: "'JetBrains Mono', monospace" }}>
                            ฿{parseFloat(bankForm.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <motion.button type="submit"
                    className={`wf-btn ${isEmerald ? "wf-btn-green" : "wf-btn-cyan"}`}
                    disabled={submitting || !bankValid}
                    whileTap={!submitting && bankValid ? { scale: 0.97 } : {}}
                  >
                    {submitting ? <><div className="wf-spinner" /> Processing…</> : <><ArrowUpFromLine size={15} /> Withdraw to Bank</>}
                  </motion.button>
                </motion.form>
              )}

              {/* USDT FORM */}
              {activeTab === "usdt" && (
                <motion.form key="usdt"
                  initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }}
                  transition={{ duration: 0.22 }}
                  onSubmit={handleUsdtSubmit}
                  style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  {/* Network Selection */}
                  <Field label="Network *">
                    <div className="wf-net-grid">
                      {USDT_NETWORKS.map((net, i) => {
                        const isSel = usdtForm.network === net.code;
                        return (
                          <motion.button key={net.code} type="button"
                            className="wf-net-btn"
                            onClick={() => setUsdtForm({ ...usdtForm, network: net.code })}
                            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.06 }}
                            whileTap={{ scale: 0.96 }}
                            style={{
                              borderColor: isSel ? `${net.color}55` : undefined,
                              background: isSel ? `${net.color}0f` : undefined,
                              boxShadow: isSel ? `0 6px 24px ${net.color}20` : undefined,
                              transform: isSel ? "translateY(-3px)" : undefined,
                            }}
                          >
                            <div style={{
                              width: 8, height: 8, borderRadius: "50%",
                              background: net.color, margin: "0 auto 7px",
                              boxShadow: isSel ? `0 0 10px ${net.color}` : "none",
                            }} />
                            <p style={{ fontSize: "13px", fontWeight: 800, color: isSel ? net.color : "#4a6880", marginBottom: 4 }}>{net.code}</p>
                            <p style={{ fontSize: "9.5px", color: "#2a4060", fontWeight: 600 }}>Fee: {net.fee}</p>
                            <p style={{ fontSize: "9px", color: "#1e3045", marginTop: 2 }}>{net.time}</p>
                          </motion.button>
                        );
                      })}
                    </div>
                  </Field>

                  {/* Wallet Address */}
                  <Field label="Wallet Address *">
                    <div className="wf-input-wrap">
                      <input className="wf-input wf-input-mono"
                        value={usdtForm.wallet_address}
                        onChange={e => setUsdtForm({ ...usdtForm, wallet_address: e.target.value })}
                        placeholder={`Enter ${usdtForm.network} wallet address`}
                        style={{ paddingRight: usdtForm.wallet_address ? "46px" : undefined }}
                      />
                      <AnimatePresence>
                        {usdtForm.wallet_address && (
                          <motion.button type="button" onClick={copyAddr}
                            initial={{ opacity: 0, scale: 0.7 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.7 }}
                            whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                            style={{
                              position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                              width: 28, height: 28, borderRadius: 8, border: "1px solid rgba(34,211,238,0.2)",
                              background: "rgba(34,211,238,0.07)", display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "pointer", color: copied ? "#34d399" : "#22d3ee", outline: "none",
                              transition: "all 0.15s ease",
                            }}
                          >
                            {copied ? <Check size={12} /> : <Copy size={12} />}
                          </motion.button>
                        )}
                      </AnimatePresence>
                    </div>
                  </Field>

                  <TextInput label="Amount (USDT)" value={usdtForm.amount}
                    onChange={e => setUsdtForm({ ...usdtForm, amount: e.target.value })}
                    type="number" prefix="$" placeholder="0.00"
                    hint="Minimum: $10 USDT" required />

                  {/* THB Rate Card */}
                  <AnimatePresence>
                    {usdtForm.amount && parseFloat(usdtForm.amount) > 0 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.26 }}
                      >
                        <div className="wf-rate-card">
                          <div>
                            <p style={{ color: "rgba(34,211,238,0.5)", fontSize: "9.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>
                              THB Equivalent
                            </p>
                            <motion.p key={thbEq} initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                              style={{ color: "#d4e8f8", fontSize: "1.35rem", fontWeight: 900, fontFamily: "'JetBrains Mono', monospace", letterSpacing: "-0.03em" }}>
                              ฿{thbEq.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </motion.p>
                          </div>
                          <div style={{ textAlign: "right" }}>
                            <p style={{ color: "#2a4060", fontSize: "9px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 5 }}>Rate</p>
                            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                              <TrendingUp size={11} style={{ color: "#22d3ee" }} />
                              <span style={{ color: "#3a7090", fontSize: "11.5px", fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>
                                1 USDT = ฿{USDT_TO_THB}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Warning */}
                  <div className="wf-alert wf-alert-warn">
                    <AlertCircle size={14} style={{ color: "#fbbf24", flexShrink: 0, marginTop: 1 }} />
                    <span>
                      Ensure wallet supports <strong>{usdtForm.network}</strong>.
                      Sending to wrong network may result in permanent fund loss.
                    </span>
                  </div>

                  <motion.button type="submit"
                    className="wf-btn wf-btn-cyan"
                    disabled={submitting || !usdtValid}
                    whileTap={!submitting && usdtValid ? { scale: 0.97 } : {}}
                  >
                    {submitting ? <><div className="wf-spinner" /> Processing…</> : <><Wallet size={15} /> Withdraw USDT</>}
                  </motion.button>
                </motion.form>
              )}
            </AnimatePresence>
          </motion.div>

          {/* ── SIDEBAR ── */}
          <div className="wf-sidebar" style={{ display: "flex", flexDirection: "column", gap: "14px" }}>

            {/* Stats */}
            <motion.div className="wf-card"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.08, duration: 0.32 }}
            >
              <div className="wf-section-title">
                <div className="wf-divider" />
                <h3 style={{ fontSize: "13px", fontWeight: 800, color: "#8ab8d4" }}>Withdrawal Summary</h3>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                <StatCard label="Total Withdrawn" value={`฿${stats.total.toLocaleString()}`} color="#10b981" icon={TrendingUp} delay={0.1} />
                <StatCard label="Pending" value={`฿${stats.pendingAmount.toLocaleString()}`} sub={`${stats.pendingCount} requests awaiting`} color="#fbbf24" icon={Clock} delay={0.16} />
              </div>
            </motion.div>

            {/* Processing Times */}
            <motion.div className="wf-card"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.14, duration: 0.32 }}
            >
              <div className="wf-section-title">
                <Clock size={13} style={{ color: "#22d3ee" }} />
                <h4 style={{ fontSize: "12.5px", fontWeight: 800, color: "#8ab8d4" }}>Processing Times</h4>
              </div>
              {[
                { icon: Building2, label: "Bank Transfer", time: "1–24 hrs",  color: "#22d3ee" },
                { icon: Zap,       label: "USDT TRC20",   time: "~1 min",    color: "#34d399" },
                { icon: Wallet,    label: "USDT ERC20",   time: "~5 min",    color: "#a78bfa" },
                { icon: RefreshCw, label: "USDT BEP20",   time: "~1 min",    color: "#fbbf24" },
              ].map(({ icon: Icon, label, time, color }) => (
                <motion.div key={label} className="wf-proc-row"
                  whileHover={{ x: 4 }}
                  transition={{ type: "spring", stiffness: 400, damping: 25 }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: `${color}10`, border: `1px solid ${color}20`,
                      display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <Icon size={12} style={{ color }} />
                    </span>
                    <span style={{ color: "#6a90b0", fontSize: "12px", fontWeight: 600 }}>{label}</span>
                  </span>
                  <span style={{ color: "#3a6080", fontSize: "11px", fontWeight: 700, fontFamily: "'JetBrains Mono', monospace" }}>{time}</span>
                </motion.div>
              ))}
            </motion.div>

            {/* Help Card */}
            <motion.div
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.32 }}
              className="wf-card"
              style={{ padding: "16px 18px" }}
            >
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: "rgba(34,211,238,0.09)", border: "1px solid rgba(34,211,238,0.18)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Shield size={15} style={{ color: "#22d3ee" }} />
                </div>
                <div>
                  <p style={{ color: "#22d3ee", fontSize: "12px", fontWeight: 700, marginBottom: 5 }}>Need Help?</p>
                  <p style={{ color: "#3a6080", fontSize: "11.5px", fontWeight: 500, lineHeight: 1.6 }}>
                    Contact support if your withdrawal takes longer than expected.
                  </p>
                </div>
              </div>
            </motion.div>

          </div>
        </div>
      </div>
    </>
  );
}